import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import { getReferralDashboardData } from "../services/referralsService";
import { mapReferral, toReferralStats } from "../services/referralMappers";

const DEFAULT_REFERRAL_LINK = "https://pbxscribe.com/ref/user123";
const DEFAULT_STATS = toReferralStats();

const DEFAULT_REFERRALS = [];

export const fetchReferralDashboardThunk = createAsyncThunk(
  "referral/fetchReferralDashboard",
  async (clientId, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await getReferralDashboardData(resolvedClientId, {
      limit: 20,
      offset: 0,
    });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to load referral data.");
    }

    const apiReferrals = Array.isArray(response?.referrals) ? response.referrals : [];
    const mappedReferrals = apiReferrals.map(mapReferral);

    return {
      referralLink: response?.referralLink || DEFAULT_REFERRAL_LINK,
      referrals: mappedReferrals,
      totalReferrals: Number(response?.total || mappedReferrals.length),
      statsData: toReferralStats(response?.stats || {}),
    };
  },
);

const initialState = {
  currentPage: 0,
  isLoadingReferrals: false,
  referrals: DEFAULT_REFERRALS,
  totalReferrals: 0,
  referralLink: DEFAULT_REFERRAL_LINK,
  statsData: DEFAULT_STATS,
  error: "",
  hasLoaded: false,
};

export const referralInitialState = initialState;

const referralSlice = createSlice({
  name: "referral",
  initialState,
  reducers: {
    setReferralCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferralDashboardThunk.pending, (state) => {
        state.isLoadingReferrals = true;
        state.error = "";
      })
      .addCase(fetchReferralDashboardThunk.fulfilled, (state, action) => {
        state.isLoadingReferrals = false;
        state.referralLink = action.payload.referralLink;
        state.referrals = action.payload.referrals;
        state.totalReferrals = action.payload.totalReferrals;
        state.statsData = action.payload.statsData;
        state.hasLoaded = true;
      })
      .addCase(fetchReferralDashboardThunk.rejected, (state, action) => {
        state.isLoadingReferrals = false;
        state.error = action.payload || "Unable to load referral data.";
        state.hasLoaded = true;
      });
  },
});

export const { setReferralCurrentPage } = referralSlice.actions;

export const selectReferralState = (state) => state.referral;

export default referralSlice.reducer;
